import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Styles from './Gráficos.module.scss';
import { faChartBar } from '@fortawesome/free-solid-svg-icons';

import Header from 'components/Header/Header';
import ChartWithTable from 'components/ChartWithTable/ChartWithTable';
import { useSelector, useDispatch } from 'react-redux';
import { Sum } from 'shared/helpers';

import { Creators as reduxProdutos } from 'store/ducks/produtos';
import { Creators as reduxEtapas } from 'store/ducks/etapas';
import PrintButton from 'components/PrintButton/PrintButton';

const PercentSum = (el, mod = 1) => (produtos) => {
  const res =
    Sum(
      produtos.map(
        (produto) => Number(produto[el]) * (Number(produto.quantidade) / mod),
      ),
    ) || 0;
  return res;
};

const ValueFormatter = (preffix = '', suffix = '', extraDecimals = 0) => (
  value,
) =>
  `${preffix} ${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: extraDecimals,
    maximumFractionDigits: extraDecimals,
  })} ${suffix}`;

export default function Gráficos({ match }) {
  const { Gráficos } = Styles;
  const { id } = match.params;

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(reduxEtapas.getEtapas(id));
    dispatch(reduxProdutos.getProdutos());
  }, [id, dispatch]);

  const etapas = useSelector((state) => state.etapas.data);
  const pem = useSelector((state) => state.etapas.pem);
  const allProdutos = useSelector((state) => state.produtos.map || {});

  const chartsRef = [useRef(), useRef()];

  return pem !== id ? null : (
    <div className={Gráficos}>
      <Header
        title={
          'GRAFICOS DO COMPARATIVO DA DISTRIBUIÇÃO E PROPORÇÃO DAS FONTES DE NITROGÊNIO'
        }
        icon={faChartBar}
      >
        <PrintButton fitElements={chartsRef} />
      </Header>
      <main>
        <ChartWithTable
          chartContainerRef={chartsRef[0]}
          settings={{
            yScale: {
              label: (v) => `${Number(v).toFixed(0)} Kg/ha`,
              title: 'Kg/ha',
            },
            series: etapas.map((e) => ({
              label: e.fenologica,
              chartLabel: `${e.fenologica}`,
            })),
            source: [
              {
                color: { red: 250, green: 128, blue: 114 },
                chartColor: { red: 250, green: 0, blue: 0 },
                label: 'K20',
                mode: 'line',
                value: PercentSum('K2O', 100),
                valueLabel: ValueFormatter(undefined, 'kg/ha', 0),
              },
              {
                color: { red: 0, green: 185, blue: 0 },
                label: 'Nítrico (NO3)',
                mode: 'column_stack',
                value: PercentSum('NO3', 100),
                valueLabel: ValueFormatter(undefined, 'kg/ha', 0),
              },
              {
                color: { red: 71, green: 108, blue: 219 },
                label: 'Amoniacal (NH4)',
                mode: 'column_stack',
                value: PercentSum('NH4', 100),
                valueLabel: ValueFormatter(undefined, 'kg/ha', 0),
              },
              {
                color: { red: 255, green: 204, blue: 19 },
                label: 'Amídico (NH2)',
                mode: 'column_stack',
                value: PercentSum('NH2', 100),
                valueLabel: ValueFormatter(undefined, 'kg/ha', 0),
              },
              {
                color: { red: 224, green: 224, blue: 224 },
                label: 'Relação N:K',
                mode: undefined,
                value: (produtos) =>
                  PercentSum('K2O', 100)(produtos) /
                  (PercentSum('N', 100)(produtos) || 1),
                valueLabel: ValueFormatter('1 : ', undefined, 2),
              },
            ],
            // Para cada etapa envia a lista de produtos
            data: etapas.map((e) =>
              ['K20', 'NO3', 'NH4', 'NH2', 'R'].map(() =>
                e.produtos
                  .filter((p) => p.fase_id === 2)
                  .map((p) => ({
                    ...p,
                    ...allProdutos[p.id],
                  })),
              ),
            ),
            title: 'TRATAMENTO SQM VITAS',
          }}
        />
        <ChartWithTable
          chartContainerRef={chartsRef[1]}
          settings={{
            yScale: {
              label: (v) => `${Number(v).toFixed(0)} Kg/ha`,
              title: 'Kg/ha',
            },
            series: etapas.map((e) => ({
              label: e.fenologica,
              chartLabel: `${e.fenologica}`,
            })),
            source: [
              {
                color: { red: 250, green: 128, blue: 114 },
                chartColor: { red: 250, green: 0, blue: 0 },
                label: 'K20',
                mode: 'line',
                value: (produtos) => PercentSum('K2O', 100)(produtos),
                valueLabel: ValueFormatter(undefined, 'kg/ha'),
              },
              {
                color: { red: 0, green: 185, blue: 0 },
                label: 'Nítrico (NO3)',
                mode: 'column_stack',
                value: PercentSum('NO3', 100),
                valueLabel: ValueFormatter(undefined, 'kg/ha'),
              },
              {
                color: { red: 71, green: 108, blue: 219 },
                label: 'Amoniacal (NH4)',
                mode: 'column_stack',
                value: PercentSum('NH4', 100),
                valueLabel: ValueFormatter(undefined, 'kg/ha'),
              },
              {
                color: { red: 255, green: 204, blue: 19 },
                label: 'Amídico (NH2)',
                mode: 'column_stack',
                value: PercentSum('NH2', 100),
                valueLabel: ValueFormatter(undefined, 'kg/ha'),
              },
              {
                color: { red: 224, green: 224, blue: 224 },
                label: 'Relação N:K',
                mode: undefined,
                value: (produtos) => {
                  const result =
                    PercentSum('K2O', 100)(produtos) /
                    PercentSum('N', 100)(produtos);
                  return result === Infinity ? 0 : result || 0;
                },
                valueLabel: ValueFormatter('1 : ', undefined, 2),
              },
            ],
            // Para cada etapa envia a lista de produtos
            data: etapas.map((e) =>
              ['K20', 'NO3', 'NH4', 'NH2', 'R'].map(() =>
                e.produtos
                  .filter((p) => p.fase_id === 3)
                  .map((p) => ({
                    ...p,
                    ...allProdutos[p.id],
                  })),
              ),
            ),
            title: 'TRATAMENTO CONVENCIONAL',
          }}
        />
      </main>
    </div>
  );
}

Gráficos.propTypes = {
  match: PropTypes.any.isRequired,
};
